import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { MutationCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ApiError } from './lib/api';
import { AuthProvider } from './lib/auth';
import { ToastProvider } from './components/ui';
import { App } from './App';
import { AppErrorBoundary, markAppLoaded } from './lib/app-update';
import './styles.css';

/** Aviso global tras cualquier guardado correcto: la sesión (permisos, plan, logo…) y las pantallas abiertas se refrescan. */
export const DATA_CHANGED_EVENT = 'wulftime:data-changed';

const queryClient = new QueryClient({
  // Tras cualquier guardado se refresca todo lo que está en pantalla (y lo demás queda marcado para recargar), para que
  // un cambio se vea al momento en todas las vistas relacionadas, no solo en la que lo hizo.
  mutationCache: new MutationCache({
    onSuccess: () => {
      void queryClient.invalidateQueries();
      window.dispatchEvent(new Event(DATA_CHANGED_EVENT));
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 15_000,
      refetchOnWindowFocus: true,
      retry: (count, err) => !(err instanceof ApiError && err.status >= 400 && err.status < 500) && count < 2,
    },
  },
});

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <AppErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ToastProvider>
          <AuthProvider>
            <App />
          </AuthProvider>
        </ToastProvider>
      </QueryClientProvider>
    </AppErrorBoundary>
  </StrictMode>,
);

markAppLoaded();

// PWA: el service worker solo cachea la interfaz (nunca respuestas de la API con datos personales).
if ('serviceWorker' in navigator && import.meta.env.PROD) {
  window.addEventListener('load', () => navigator.serviceWorker.register('/sw.js').catch(() => {}));
}
